import React, { useLayoutEffect, useRef } from 'react';
import { formatPhone, displayPhone } from '../phone.js';

// Drop-in replacement for a phone/fax <input>: shows the stored value in
// (438) 664-0000 form and masks as the user types. onChange receives the
// formatted string (not an event), which is what gets saved.
//
// Keeps the caret where the user is editing — a naive mask jumps the caret
// to the end on every keystroke, and backspacing over ")" or "-" would do
// nothing because the digits don't change. Here the caret is tracked by
// "how many digits are before it", and deleting a formatting character
// deletes the digit in front of it instead.
export default function PhoneInput({ value, onChange, placeholder = '(000) 000-0000', ...rest }) {
  const ref = useRef(null);
  const caretDigits = useRef(null);

  useLayoutEffect(() => {
    const input = ref.current;
    if (caretDigits.current === null || !input || document.activeElement !== input) return;
    let pos = 0, seen = 0;
    const v = input.value;
    while (pos < v.length && seen < caretDigits.current) {
      if (/\d/.test(v[pos])) seen++;
      pos++;
    }
    input.setSelectionRange(pos, pos);
    caretDigits.current = null;
  });

  const handleChange = (e) => {
    const el = e.target;
    let raw = el.value;
    let caret = el.selectionStart ?? raw.length;
    const prev = displayPhone(value);
    const deletedFormatter =
      raw.length === prev.length - 1 &&
      raw.replace(/\D/g, '') === prev.replace(/\D/g, '') &&
      e.nativeEvent?.inputType === 'deleteContentBackward';
    if (deletedFormatter) {
      // Remove the nearest digit before the caret instead.
      let i = caret - 1;
      while (i >= 0 && !/\d/.test(raw[i])) i--;
      if (i >= 0) { raw = raw.slice(0, i) + raw.slice(i + 1); caret = i; }
    }
    caretDigits.current = raw.slice(0, caret).replace(/\D/g, '').length;
    onChange(formatPhone(raw));
  };

  return (
    <input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      {...rest}
      value={displayPhone(value)}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
}
